import { DialogComponent } from '@theme/dialog';
import { normalizeString } from '@theme/utilities';

const STORAGE_KEY = 'shopify_market_confirmation_v1';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * First-visit market confirmation dialog.
 * Reads Shopify's current localization and persists the customer's choice in localStorage.
 *
 * @typedef {object} ConfirmationState
 * @property {number} version
 * @property {string} countryCode
 * @property {number} confirmedAt
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} confirmView
 * @property {HTMLElement} selectorView
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLElement} confirmTitle
 * @property {HTMLElement} selectorTitle
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} countryInput
 * @property {HTMLInputElement} [search]
 * @property {HTMLElement} countryList
 * @property {HTMLElement} [noResults]
 *
 * @extends {DialogComponent}
 */
class MarketConfirmationPopup extends DialogComponent {
  /** @type {boolean} */
  #skipPersist = false;

  /** @type {(() => Promise<void>) | null} */
  #inheritedCloseDialog = null;

  connectedCallback() {
    super.connectedCallback();
    this.#wrapCloseDialog();

    document.addEventListener('shopify:section:select', this.#onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);

    if (this.#isDesignMode) return;

    if (this.#shouldShow()) {
      this.showDialog();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
    super.disconnectedCallback();
  }

  /**
   * Shows the dialog and moves focus to the primary action.
   */
  showDialog() {
    super.showDialog();
    this.addEventListener(
      'dialog:open',
      () => {
        if (!this.refs.selectorView.hidden) {
          this.#focusSelector();
          return;
        }

        this.refs.confirmButton?.focus();
      },
      { once: true }
    );
  }

  /**
   * Confirms the currently localized country and closes the dialog.
   */
  confirmCountry = () => {
    this.closeDialog();
  };

  /**
   * Switches from the confirm view to the country selector view.
   */
  showCountrySelector = () => {
    this.refs.confirmView.hidden = true;
    this.refs.selectorView.hidden = false;
    this.#syncLabelledBy();
    this.#focusSelector();
  };

  /**
   * Persists the selected country, then submits Shopify's localization form.
   *
   * @param {string} countryCode
   * @param {Event} event
   */
  selectCountry = (countryCode, event) => {
    event.preventDefault();

    if (typeof countryCode !== 'string' || countryCode.length === 0) return;

    this.#writeStoredConfirmation(countryCode);
    this.refs.countryInput.value = countryCode;
    this.refs.form.requestSubmit();
  };

  /**
   * Filters the rendered country list against the search field value.
   *
   * @param {Event} event
   */
  filterCountries = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const query = event.target.value.trim();
    this.#applyCountryFilter(query);
  };

  /**
   * Moves keyboard focus through visible country options.
   *
   * @param {KeyboardEvent} event
   */
  onCountryListKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const buttons = this.#visibleCountryButtons();
    if (buttons.length === 0) return;

    event.preventDefault();

    const activeButton =
      event.target instanceof Element ? event.target.closest('[data-country-code]') : null;
    const currentIndex = buttons.indexOf(/** @type {HTMLButtonElement} */ (activeButton));
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0 ? 0 : (currentIndex + offset + buttons.length) % buttons.length;

    buttons[nextIndex]?.focus();
  };

  /**
   * Moves focus from the search field into the country list.
   *
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.#visibleCountryButtons()[0]?.click();
      return;
    }

    if (event.key !== 'ArrowDown') return;

    const firstButton = this.#visibleCountryButtons()[0];
    if (!firstButton) return;

    event.preventDefault();
    firstButton.focus();
  };

  /**
   * Wraps DialogComponent.closeDialog so X, Escape, and backdrop persist the current country.
   * DialogComponent assigns closeDialog as an instance field, so super.closeDialog() is not available.
   */
  #wrapCloseDialog() {
    if (this.#inheritedCloseDialog) return;

    this.#inheritedCloseDialog = this.closeDialog;

    this.closeDialog = async () => {
      const wasOpen = Boolean(this.refs.dialog?.open);

      if (wasOpen && !this.#skipPersist) {
        this.#writeStoredConfirmation(this.#countryIsoCode);
      }

      this.#resetViews();
      await this.#inheritedCloseDialog?.();
    };
  }

  /**
   * @returns {boolean}
   */
  get #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  /**
   * @returns {string}
   */
  get #countryIsoCode() {
    return this.dataset.countryIso ?? '';
  }

  /**
   * @returns {number}
   */
  get #expiryDays() {
    const parsed = Number(this.dataset.expiryDays);
    if (!Number.isFinite(parsed)) return 30;
    return Math.min(365, Math.max(1, Math.round(parsed)));
  }

  /**
   * @returns {boolean}
   */
  #shouldShow() {
    if (this.#shouldHideForUnitedKingdom()) return false;

    const stored = this.#readStoredConfirmation();
    if (!stored) return true;
    if (stored.countryCode !== this.#countryIsoCode) return true;

    return Date.now() - stored.confirmedAt > this.#expiryDays * DAY_IN_MS;
  }

  /**
   * UK visitors are already on the United Kingdom market (GBP) via Shopify localization.
   * @returns {boolean}
   */
  #shouldHideForUnitedKingdom() {
    return this.dataset.hideForUnitedKingdom === 'true' && this.#countryIsoCode === 'GB';
  }

  /**
   * @returns {ConfirmationState | null}
   */
  #readStoredConfirmation() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const isValid =
        parsed &&
        typeof parsed === 'object' &&
        typeof parsed.countryCode === 'string' &&
        parsed.countryCode.length > 0 &&
        typeof parsed.confirmedAt === 'number' &&
        Number.isFinite(parsed.confirmedAt);

      if (!isValid) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return /** @type {ConfirmationState} */ (parsed);
    } catch {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storefront must remain usable when storage is unavailable or corrupt.
      }

      return null;
    }
  }

  /**
   * @param {string} countryCode
   */
  #writeStoredConfirmation(countryCode) {
    if (this.#isDesignMode || !countryCode) return;

    try {
      const payload = {
        version: 1,
        countryCode,
        confirmedAt: Date.now(),
      };

      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Storefront must remain usable when storage is unavailable.
    }
  }

  #resetViews() {
    this.refs.confirmView.hidden = false;
    this.refs.selectorView.hidden = true;
    this.#syncLabelledBy();

    if (this.refs.search) {
      this.refs.search.value = '';
      this.#applyCountryFilter('');
    }
  }

  /**
   * @param {string} query
   */
  #applyCountryFilter(query) {
    const normalizedQuery = normalizeString(query);
    const buttons = this.#countryButtons();
    let visibleCount = 0;

    for (const button of buttons) {
      const isMatch = normalizedQuery.length === 0 || this.#matchesQuery(button, normalizedQuery);
      const item = button.closest('[data-country-item]');

      if (item instanceof HTMLElement) {
        item.hidden = !isMatch;
      }

      if (isMatch) visibleCount += 1;
    }

    if (this.refs.noResults) {
      this.refs.noResults.hidden = visibleCount > 0;
    }
  }

  #syncLabelledBy() {
    const title = this.refs.selectorView.hidden ? this.refs.confirmTitle : this.refs.selectorTitle;
    if (title) {
      this.refs.dialog.setAttribute('aria-labelledby', title.id);
    }
  }

  #focusSelector() {
    if (this.refs.search) {
      this.refs.search.focus();
      return;
    }

    this.#visibleCountryButtons()[0]?.focus();
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #countryButtons() {
    return Array.from(this.refs.countryList?.querySelectorAll('[data-country-code]') ?? []).filter(
      (element) => element instanceof HTMLButtonElement
    );
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #visibleCountryButtons() {
    return this.#countryButtons().filter((button) => {
      const item = button.closest('[data-country-item]');
      return !(item instanceof HTMLElement) || !item.hidden;
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string} query
   * @returns {boolean}
   */
  #matchesQuery(button, query) {
    const name = normalizeString(button.dataset.countryName ?? '');
    const iso = normalizeString(button.dataset.countryCode ?? '');
    const currency = normalizeString(button.dataset.countryCurrency ?? '');
    const aliases = (button.dataset.countryAliases ?? '')
      .split(',')
      .map((alias) => normalizeString(alias))
      .filter(Boolean);

    return (
      name.includes(query) ||
      iso === query ||
      currency.includes(query) ||
      aliases.some((alias) => alias.startsWith(query) || alias === query)
    );
  }

  /**
   * @param {Event} event
   */
  #onSectionSelect = (event) => {
    const sectionId = this.#eventSectionId(event);
    if (sectionId !== this.dataset.sectionId) return;

    this.#resetViews();
    this.showDialog();
  };

  /**
   * @param {Event} event
   */
  #onSectionDeselect = (event) => {
    const sectionId = this.#eventSectionId(event);
    if (sectionId !== this.dataset.sectionId) return;

    this.#skipPersist = true;
    this.closeDialog();
    this.#skipPersist = false;
  };

  /**
   * @param {Event} event
   * @returns {string | undefined}
   */
  #eventSectionId(event) {
    if (!(event instanceof CustomEvent)) return undefined;
    return event.detail?.sectionId;
  }
}

if (!customElements.get('market-confirmation-popup')) {
  customElements.define('market-confirmation-popup', MarketConfirmationPopup);
}
